import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChevronRight, RefreshCw, CheckCircle, XCircle, AlertCircle,
  Link2, Calendar, Download,
} from 'lucide-react'
import { toast } from 'sonner'
import { api } from '@/lib/api/client'
import { config } from '@/lib/config'
import { BrandGlyph } from './IntegrationsPage'

const GLYPH = {
  monogram: 'ADO',
  gradient: 'linear-gradient(135deg, oklch(0.55 0.18 250) 0%, oklch(0.42 0.2 260) 100%)',
}

type Tab = 'projects' | 'work-items' | 'log'

interface AdoStatus {
  connected:      boolean
  orgUrl:         string | null
  projectCount:   number
  workItemCount:  number
  lastSyncAt:     string | null
  lastSyncStatus: string | null
  lastSyncError:  string | null
}

interface AdoProject {
  id:               string
  name:             string
  state:            string | null
  visibility:       string | null
  forgeProjectId:   number | null
  forgeProjectName: string | null
  workItemCount:    number
}

interface AdoWorkItem {
  id:            number
  projectId:     string
  projectName:   string
  title:         string
  workItemType:  string
  state:         string
  assignedTo:    string | null
  iterationPath: string | null
}

interface SyncLogEntry {
  id:              number
  entity:          string
  syncedAt:        string
  recordsUpserted: number
  status:          string
  errorMessage:    string | null
}

interface ForgeProject {
  id:   number
  name: string
}

function relTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 2)  return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)  return `${hrs}h ago`
  return new Date(iso).toLocaleDateString('sv-SE')
}

const TYPE_COLORS: Record<string, string> = {
  'Epic':       'oklch(0.62 0.18 285)',
  'Feature':    'oklch(0.62 0.14 200)',
  'User Story': 'oklch(0.66 0.13 150)',
  'Task':       'oklch(0.72 0.052 198)',
  'Bug':        'oklch(0.62 0.18 25)',
  'Test Case':  'oklch(0.65 0.14 50)',
}

function TypeBadge({ type }: { type: string }) {
  const color = TYPE_COLORS[type] ?? 'var(--ns-text-muted)'
  return (
    <span style={{
      fontSize: 10.5, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
      background: `color-mix(in oklch, ${color} 15%, transparent)`,
      color,
      border: `1px solid color-mix(in oklch, ${color} 30%, transparent)`,
    }}>{type}</span>
  )
}

export default function AzureDevOpsPage() {
  const qc = useQueryClient()
  const [tab,    setTab]    = useState<Tab>('projects')
  const [orgUrl, setOrgUrl] = useState('')
  const [search, setSearch] = useState('')
  const [linkingProject, setLinkingProject]           = useState<string | null>(null)
  const [selectedForgeProject, setSelectedForgeProject] = useState<string>('')

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['azure-devops', 'status'],
    queryFn:  () => api.get<AdoStatus>('/azure-devops/status'),
  })
  const { data: projects = [] } = useQuery({
    queryKey: ['azure-devops', 'projects'],
    queryFn:  () => api.get<AdoProject[]>('/azure-devops/projects'),
    enabled:  status?.connected === true,
  })
  const { data: workItemsResp } = useQuery({
    queryKey: ['azure-devops', 'work-items', 'admin'],
    queryFn:  () => api.get<{ total: number; top: number; offset: number; items: AdoWorkItem[] }>('/azure-devops/work-items?top=1000'),
    enabled:  status?.connected === true && tab === 'work-items',
  })
  const workItems: AdoWorkItem[] = workItemsResp?.items ?? []
  const { data: syncLog = [] } = useQuery({
    queryKey: ['azure-devops', 'sync-log'],
    queryFn:  () => api.get<SyncLogEntry[]>('/azure-devops/sync-log'),
    enabled:  status?.connected === true && tab === 'log',
  })
  const { data: forgeProjects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn:  () => api.get<ForgeProject[]>('/projects'),
    enabled:  status?.connected === true,
    staleTime: 5 * 60 * 1000,
  })

  const configureMutation = useMutation({
    mutationFn: (url: string) => api.post<unknown>('/azure-devops/configure', { orgUrl: url }),
    onSuccess: () => {
      setOrgUrl('')
      toast.success('Connected — run a sync to import projects and work items')
      qc.invalidateQueries({ queryKey: ['azure-devops'] })
    },
    onError: () => toast.error('Failed to save configuration'),
  })
  const disconnectMutation = useMutation({
    mutationFn: () => api.delete<unknown>('/azure-devops/connect'),
    onSuccess: () => { toast.success('Disconnected'); qc.invalidateQueries({ queryKey: ['azure-devops'] }) },
  })
  const syncMutation = useMutation({
    mutationFn: () => api.post<unknown>('/azure-devops/sync', {}),
    onSuccess: () => { toast.success('Sync complete'); qc.invalidateQueries({ queryKey: ['azure-devops'] }) },
    onError:   () => toast.error('Sync failed'),
  })
  const linkMutation = useMutation({
    mutationFn: ({ adoId, forgeId }: { adoId: string; forgeId: number | null }) =>
      api.put<unknown>(`/azure-devops/projects/${adoId}/link`, { forgeProjectId: forgeId }),
    onSuccess: () => {
      setLinkingProject(null)
      setSelectedForgeProject('')
      toast.success('Project linked')
      qc.invalidateQueries({ queryKey: ['azure-devops', 'projects'] })
    },
  })

  const handleDisconnect = () => {
    if (!confirm('Disconnect Azure DevOps? Synced data will be retained but no new syncs will run.')) return
    disconnectMutation.mutate()
  }

  if (statusLoading) {
    return <div className="page"><Breadcrumb /><p className="muted" style={{ paddingTop: 32, textAlign: 'center' }}>Loading…</p></div>
  }

  // ── Setup screen ─────────────────────────────────────────────────────────────
  if (!status?.connected) {
    return (
      <div className="page">
        <Breadcrumb />
        <div className="card" style={{ maxWidth: 520, padding: 32, textAlign: 'center' }}>
          <div style={{ margin: '0 auto 16px', width: 48 }}><BrandGlyph {...GLYPH} size={48} /></div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Connect Azure DevOps</h2>
          <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6, marginBottom: 24 }}>
            Sync projects and work items from your Azure DevOps organisation.
            Authentication uses the Forge managed identity — no credentials needed.
          </p>
          <div style={{ background: 'var(--ns-surface-2)', borderRadius: 8, padding: 16, marginBottom: 24, textAlign: 'left' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Before you connect</div>
            <ol style={{ fontSize: 13, lineHeight: 1.8, paddingLeft: 18 }}>
              <li>Add <strong>{new URL(config.apiUrl).hostname.split('.')[0]}</strong> as a member of your ADO organisation</li>
              <li>Grant it access to the projects you want to sync</li>
            </ol>
          </div>
          <div style={{ textAlign: 'left', marginBottom: 16 }}>
            <label className="muted" style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>
              Organisation URL
            </label>
            <input
              className="input"
              placeholder="https://dev.azure.com/your-org  or  https://your-org.visualstudio.com"
              value={orgUrl}
              onChange={e => setOrgUrl(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <button
            className="btn btn-brand"
            style={{ gap: 8, width: '100%', justifyContent: 'center', height: 40 }}
            disabled={!orgUrl.trim() || configureMutation.isPending}
            onClick={() => configureMutation.mutate(orgUrl.trim())}
          >
            {configureMutation.isPending ? <><RefreshCw size={14} style={{ animation: 'spin 0.8s linear infinite' }} />Connecting…</> : 'Connect to Azure DevOps'}
          </button>
          {configureMutation.isError && <p style={{ color: 'var(--ns-err)', fontSize: 12.5, marginTop: 10 }}>Failed to save — check the org URL.</p>}
        </div>
      </div>
    )
  }

  // ── Connected screen ──────────────────────────────────────────────────────────
  const lastSyncTime = status.lastSyncAt ? new Date(status.lastSyncAt).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }) : null
  const lastSyncDate = status.lastSyncAt ? new Date(status.lastSyncAt).toLocaleDateString('sv-SE') : null

  const filtered = {
    projects:  projects.filter(p => p.name.toLowerCase().includes(search.toLowerCase())),
    workItems: workItems.filter(w =>
      w.title.toLowerCase().includes(search.toLowerCase()) ||
      w.projectName.toLowerCase().includes(search.toLowerCase()) ||
      (w.assignedTo?.toLowerCase().includes(search.toLowerCase()) ?? false)
    ),
  }

  return (
    <div className="page">
      <Breadcrumb />

      {/* Hero header */}
      <div className="card" style={{
        padding: 24, marginBottom: 20,
        background: 'linear-gradient(135deg, var(--ns-surface) 0%, var(--ns-surface-2) 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, background: 'radial-gradient(circle, oklch(0.55 0.18 250 / 0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="row" style={{ gap: 18, alignItems: 'flex-start', position: 'relative' }}>
          <BrandGlyph {...GLYPH} size={56} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="row" style={{ gap: 10, alignItems: 'center', marginBottom: 4 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em' }}>Azure DevOps</h1>
              <span className="badge badge-ok"><span className="dot dot-ok" />Connected</span>
            </div>
            <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>
              Projects and work items. Synced daily at 4 AM.
            </p>
            <div className="row" style={{ gap: 12, marginTop: 12, fontSize: 11.5, color: 'var(--ns-text-muted)' }}>
              <span className="row" style={{ gap: 5 }}><Link2 size={12} />{status.orgUrl}</span>
              {status.lastSyncAt && <>
                <span style={{ width: 1, height: 12, background: 'var(--ns-border)' }} />
                <span className="row" style={{ gap: 5 }}><Calendar size={12} />Synced {lastSyncDate}</span>
              </>}
            </div>
          </div>
          <div className="row" style={{ gap: 6, flexShrink: 0 }}>
            <button className="btn btn-ghost btn-sm" style={{ gap: 5, color: 'var(--ns-err)' }}
              onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
              <Link2 size={13} />Disconnect
            </button>
            <button className="btn btn-brand btn-sm" style={{ gap: 5 }}
              onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
              <RefreshCw size={13} style={{ animation: syncMutation.isPending ? 'spin 0.8s linear infinite' : undefined }} />
              {syncMutation.isPending ? 'Syncing…' : 'Sync now'}
            </button>
          </div>
        </div>
      </div>

      {/* Stat row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 22 }}>
        <StatCard label="Projects"   value={String(status.projectCount)}  sub="Synced from ADO" />
        <StatCard label="Work items" value={String(status.workItemCount)} sub="Across all projects" />
        <StatCard label="Last sync"  value={lastSyncTime ?? '—'}          sub={lastSyncDate ?? 'Never'} />
        <StatCard label="Status" isCustom
          value={
            status.lastSyncStatus === 'success'
              ? <span className="badge badge-ok" style={{ fontSize: 13, height: 26, padding: '0 10px' }}><span className="dot dot-ok" />Healthy</span>
              : <span className="badge badge-err" style={{ fontSize: 13, height: 26, padding: '0 10px' }}><span className="dot dot-err" />{status.lastSyncStatus ?? '—'}</span>
          }
          sub={status.lastSyncError ?? 'Daily at 4 AM'} />
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 0 }}>
        {([
          ['projects',   'Projects',   projects.length],
          ['work-items', 'Work Items', status.workItemCount],
          ['log',        'Sync Log',   null],
        ] as [Tab, string, number | null][]).map(([id, label, count]) => (
          <button key={id} className={`tab${tab === id ? ' active' : ''}`} onClick={() => { setTab(id); setSearch('') }}>
            {label}{count !== null && <span className="count">{count}</span>}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 18 }}>
        {tab === 'projects'   && <ProjectsTab   rows={filtered.projects}  search={search} onSearch={setSearch}
                                                 forgeProjects={forgeProjects} linkingProject={linkingProject}
                                                 selectedForgeProject={selectedForgeProject}
                                                 setLinkingProject={setLinkingProject}
                                                 setSelectedForgeProject={setSelectedForgeProject}
                                                 onSaveLink={(adoId, forgeId) => linkMutation.mutate({ adoId, forgeId })} />}
        {tab === 'work-items' && <WorkItemsTab  rows={filtered.workItems} search={search} onSearch={setSearch} />}
        {tab === 'log'        && <SyncLogTab    log={syncLog} />}
      </div>
    </div>
  )
}

// ── Projects tab ───────────────────────────────────────────────────────────────

function ForgeProjectPicker({ forgeProjects, value, onChange }: {
  forgeProjects: ForgeProject[]
  value: string
  onChange: (id: string) => void
}) {
  const [q, setQ] = React.useState(() => {
    const found = forgeProjects.find(p => String(p.id) === value)
    return found ? found.name : ''
  })
  const [open, setOpen] = React.useState(false)

  const matches = q.length > 0
    ? forgeProjects.filter(p => p.name.toLowerCase().includes(q.toLowerCase())).slice(0, 12)
    : forgeProjects.slice(0, 12)

  const select = (fp: ForgeProject) => {
    onChange(String(fp.id))
    setQ(fp.name)
    setOpen(false)
  }
  const clear = () => { onChange(''); setQ(''); setOpen(false) }

  return (
    <div style={{ position: 'relative', width: 280 }}>
      <div className="row" style={{ gap: 0, height: 28, border: '1px solid var(--ns-border)', borderRadius: 6, background: 'var(--ns-surface)', overflow: 'hidden' }}>
        <input
          value={q}
          onChange={e => { setQ(e.target.value); onChange(''); setOpen(true) }}
          onFocus={() => setOpen(true)}
          placeholder="Search Forge project…"
          style={{ flex: 1, padding: '0 8px', fontSize: 12, border: 'none', background: 'transparent', outline: 'none' }}
        />
        {value && (
          <button onClick={clear} style={{ padding: '0 8px', fontSize: 11, color: 'var(--ns-text-muted)', background: 'transparent', border: 'none', cursor: 'pointer' }}>✕</button>
        )}
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          background: 'var(--ns-surface)', border: '1px solid var(--ns-border)',
          borderRadius: 6, boxShadow: '0 4px 16px oklch(0 0 0 / 0.12)',
          maxHeight: 240, overflow: 'auto', marginTop: 2,
        }}>
          <div
            className="muted"
            style={{ padding: '6px 10px', fontSize: 11.5, cursor: 'pointer', borderBottom: '1px solid var(--ns-border)' }}
            onMouseDown={e => { e.preventDefault(); clear() }}
          >
            — Unlink —
          </div>
          {matches.map(fp => (
            <div
              key={fp.id}
              onMouseDown={e => { e.preventDefault(); select(fp) }}
              style={{
                padding: '7px 10px', fontSize: 12.5, cursor: 'pointer',
                background: String(fp.id) === value ? 'var(--ns-brand-soft)' : undefined,
                color: String(fp.id) === value ? 'var(--ns-brand-strong)' : undefined,
              }}
              onMouseEnter={e => { if (String(fp.id) !== value) (e.currentTarget as HTMLElement).style.background = 'var(--ns-surface-2)' }}
              onMouseLeave={e => { if (String(fp.id) !== value) (e.currentTarget as HTMLElement).style.background = '' }}
            >
              {fp.name}
            </div>
          ))}
          {matches.length === 0 && <div className="muted" style={{ padding: '8px 10px', fontSize: 12 }}>No matches</div>}
        </div>
      )}
    </div>
  )
}

function ProjectsTab({ rows, search, onSearch, forgeProjects, linkingProject, selectedForgeProject, setLinkingProject, setSelectedForgeProject, onSaveLink }: {
  rows: AdoProject[]
  search: string
  onSearch: (v: string) => void
  forgeProjects: ForgeProject[]
  linkingProject: string | null
  selectedForgeProject: string
  setLinkingProject: (id: string | null) => void
  setSelectedForgeProject: (v: string) => void
  onSaveLink: (adoId: string, forgeId: number | null) => void
}) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'visible' }}>
      <div className="row" style={{ padding: '12px 14px', borderBottom: '1px solid var(--ns-border)', gap: 8 }}>
        <SearchInput value={search} onChange={onSearch} placeholder="Search projects…" width={260} />
        <div style={{ flex: 1 }} />
        <span className="muted tabular" style={{ fontSize: 11.5 }}>Showing {rows.length} of {rows.length}</span>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Project</th>
            <th style={{ width: 100 }}>State</th>
            <th style={{ width: 100 }}>Work items</th>
            <th style={{ width: 320 }}>Linked to</th>
            <th style={{ width: 100 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(p => (
            <tr key={p.id}>
              <td style={{ fontWeight: 500 }}>{p.name}</td>
              <td className="muted" style={{ fontSize: 12 }}>{p.state ?? '—'}</td>
              <td className="mono tabular muted" style={{ fontSize: 12 }}>{p.workItemCount}</td>
              <td>
                {linkingProject === p.id ? (
                  <div className="row" style={{ gap: 6 }}>
                    <ForgeProjectPicker
                      forgeProjects={forgeProjects}
                      value={selectedForgeProject}
                      onChange={setSelectedForgeProject}
                    />
                    <button className="btn btn-brand btn-sm"
                      onClick={() => onSaveLink(p.id, selectedForgeProject ? Number(selectedForgeProject) : null)}>
                      Save
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setLinkingProject(null)}>Cancel</button>
                  </div>
                ) : p.forgeProjectName ? (
                  <span className="row" style={{ gap: 5, fontSize: 12, color: 'var(--ns-brand-strong)' }}>
                    <Link2 size={12} />{p.forgeProjectName}
                  </span>
                ) : (
                  <span className="muted" style={{ fontSize: 12 }}>—</span>
                )}
              </td>
              <td style={{ textAlign: 'right' }}>
                {linkingProject !== p.id && (
                  <button className="btn btn-ghost btn-sm" onClick={() => {
                    setLinkingProject(p.id)
                    setSelectedForgeProject(p.forgeProjectId ? String(p.forgeProjectId) : '')
                  }}>
                    {p.forgeProjectName ? 'Change' : 'Link project'}
                  </button>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 40 }}>No projects — run a sync to import</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

// ── Work items tab ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 100

function WorkItemsTab({ rows, search, onSearch }: {
  rows: AdoWorkItem[]
  search: string
  onSearch: (v: string) => void
}) {
  const [page, setPage] = React.useState(1)
  React.useEffect(() => { setPage(1) }, [search])

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const paged      = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="row" style={{ padding: '12px 14px', borderBottom: '1px solid var(--ns-border)', gap: 8 }}>
        <SearchInput value={search} onChange={onSearch} placeholder="Search work items…" width={260} />
        <div style={{ flex: 1 }} />
        <span className="muted tabular" style={{ fontSize: 11.5 }}>{rows.length.toLocaleString()} items</span>
        <button className="btn btn-ghost btn-sm"><Download size={13} />Export</button>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 70 }}>ID</th>
            <th style={{ width: 180 }}>Project</th>
            <th>Title</th>
            <th style={{ width: 110 }}>Type</th>
            <th style={{ width: 100 }}>State</th>
            <th style={{ width: 140 }}>Assigned to</th>
          </tr>
        </thead>
        <tbody>
          {paged.map(wi => (
            <tr key={wi.id}>
              <td className="mono tabular muted" style={{ fontSize: 12 }}>{wi.id}</td>
              <td className="muted" style={{ fontSize: 12 }}>{wi.projectName}</td>
              <td style={{ fontWeight: 500 }}>{wi.title}</td>
              <td><TypeBadge type={wi.workItemType} /></td>
              <td className="muted" style={{ fontSize: 12 }}>{wi.state}</td>
              <td className="muted" style={{ fontSize: 12 }}>{wi.assignedTo ?? '—'}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 40 }}>No work items — run a sync to import</td></tr>
          )}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div className="row" style={{ padding: '10px 14px', borderTop: '1px solid var(--ns-border)', gap: 8, justifyContent: 'space-between' }}>
          <span className="muted" style={{ fontSize: 12 }}>
            {((page - 1) * PAGE_SIZE + 1).toLocaleString()}–{Math.min(page * PAGE_SIZE, rows.length).toLocaleString()} of {rows.length.toLocaleString()}
          </span>
          <div className="row" style={{ gap: 4 }}>
            <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span className="muted tabular" style={{ fontSize: 12, padding: '0 6px' }}>{page} / {totalPages}</span>
            <button className="btn btn-ghost btn-sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Sync log tab ───────────────────────────────────────────────────────────────

function SyncLogTab({ log }: { log: SyncLogEntry[] }) {
  const [entityFilter, setEntityFilter] = useState<string>('all')
  const entities = Array.from(new Set(log.map(l => l.entity))).sort()
  const filtered  = entityFilter === 'all' ? log : log.filter(l => l.entity === entityFilter)

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="row" style={{ padding: '12px 14px', borderBottom: '1px solid var(--ns-border)', gap: 6 }}>
        <button className={`chip${entityFilter === 'all' ? ' active' : ''}`} onClick={() => setEntityFilter('all')}>All</button>
        {entities.map(e => (
          <button key={e} className={`chip${entityFilter === e ? ' active' : ''}`} onClick={() => setEntityFilter(e)}
            style={{ textTransform: 'capitalize' }}>{e}</button>
        ))}
      </div>
      <div style={{ maxHeight: 540, overflow: 'auto' }}>
        {filtered.map(l => (
          <div key={l.id} className="row" style={{ padding: '10px 14px', borderTop: '1px solid var(--ns-border)', gap: 12 }}>
            {l.status === 'success'
              ? <CheckCircle size={14} style={{ color: 'var(--ns-ok)', flexShrink: 0 }} />
              : <AlertCircle size={14} style={{ color: 'var(--ns-err)', flexShrink: 0 }} />
            }
            <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 4, background: 'var(--ns-surface-2)', color: 'var(--ns-text-muted)', border: '1px solid var(--ns-border)', fontWeight: 500, minWidth: 80, textAlign: 'center', flexShrink: 0, textTransform: 'capitalize' }}>
              {l.entity}
            </span>
            <span style={{ flex: 1, fontSize: 12.5 }}>
              {l.recordsUpserted > 0
                ? <>Synced <strong className="tabular">{l.recordsUpserted}</strong> records</>
                : l.errorMessage
                  ? <span style={{ color: 'var(--ns-err)' }}>{l.errorMessage}</span>
                  : <span className="muted">No changes</span>
              }
            </span>
            <span className="mono tabular muted" style={{ fontSize: 11 }}>
              {new Date(l.syncedAt).toLocaleString('sv-SE', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
            {l.status !== 'success' && (
              <XCircle size={13} style={{ color: 'var(--ns-err)', flexShrink: 0 }} />
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="muted" style={{ textAlign: 'center', padding: 32 }}>No sync history yet</div>
        )}
      </div>
    </div>
  )
}

// ── Shared helpers ─────────────────────────────────────────────────────────────

function Breadcrumb() {
  return (
    <div className="row" style={{ gap: 4, marginBottom: 14, color: 'var(--ns-text-muted)', fontSize: 12 }}>
      <Link to="/settings/integrations" style={{ color: 'inherit' }}>Settings</Link>
      <ChevronRight size={11} />
      <Link to="/settings/integrations" style={{ color: 'inherit' }}>Integrations</Link>
      <ChevronRight size={11} />
      <span style={{ color: 'var(--ns-text)' }}>Azure DevOps</span>
    </div>
  )
}

function StatCard({ label, value, sub, isCustom }: {
  label: string; value?: React.ReactNode; sub?: string; isCustom?: boolean
}) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="muted" style={{ fontSize: 11, fontWeight: 500, marginBottom: 6, letterSpacing: '0.01em' }}>{label}</div>
      {isCustom
        ? <div style={{ marginTop: 2 }}>{value}</div>
        : <div className="mono tabular" style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</div>
      }
      {sub && <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>{sub}</div>}
    </div>
  )
}

function SearchInput({ value, onChange, placeholder, width = 280 }: {
  value: string; onChange: (v: string) => void; placeholder: string; width?: number
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 10, height: 30, border: '1px solid var(--ns-border)', borderRadius: 6, background: 'var(--ns-surface)', width }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ns-text-subtle)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
      </svg>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ flex: 1, height: '100%', border: 'none', background: 'transparent', outline: 'none', fontSize: 13 }} />
    </div>
  )
}

import React from 'react'
